import React from "react";
import {useState, useRef, useEffect} from "react";
import { AnimatePresence, motion } from "framer-motion";

const services = [
    {
        text: "Clickup",
    },
    {
        text: "Monday.com",
    },
    {
        text: "Asana",
    }
]

const variants = {
    hidden: { opacity: 0, translateY: 100 },
    visible: { opacity: 1, translateY: 0 },
    exit: { opacity: 0, translateY: -100 },
}

const AnimationText = () => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
        }, 2000);

        return () => clearInterval(intervalId);
    }, []);


    return (
        <span className="inline-block pl-2 h-[18px] md:h-[24px]">
            <span className="w-[100px] md:w-[140px] h-[22px] md:h-[30px] relative overflow-hidden flex justify-start items-center">
                <AnimatePresence>
                    <motion.span key={currentIndex} className="text-white absolute" variants={variants} initial="hidden" animate="visible" exit="exit">{services[currentIndex].text}</motion.span>
                </AnimatePresence>
            </span>
        </span>
    )
}


const tabsContent = [
    {
        title: "Improve Visibility & Reporting",
        tabTitle: "Improve Visibility",
        description: "Visibility should never be something you have to chase. ReadyPMO brings your projects into a clear view so you can understand progress, spot risks early, and make confident decisions without hunting for information.",
        points: [
            {
                title: "Live dashboards that track projects, timelines, and resource health "
            },
            {
                title: "Automated status reports & progress updates that keep leadership informed without unnecessary meetings"
            },
            {
                title: " KPI-driven report that translates project data into actionable steps"
            }
        ]
    },
    {
        title: "Stop Scope Creep & Delivery Delays",
        tabTitle: "Stop Scope Creep",
        description: "Its no secret that scope creep not so quietly erodes timelines, budgets, and strains team bandwidth. We bring structure to your projects so expectations stay clear, changes are managed properly, and your team can deliver the work that was actually agreed upon and not buried in 300 emails.",
        points: [
            {
                title: "Defined and documented change control processes"
            },
            {
                title: "Transparent communication workflows that keep everyone aligned"
            },
            {
                title: "Pre-built task workflows with built-in guardrails"
            }
        ]
    },
    {
        title: "Standardize Processes Across Teams",
        tabTitle: "Standardize Process",
        description: "When every project is ran a little differently, teams lose time, repeat mistakes, and client experiences can vary widely. ReadyPMO creates consistent processes that give your organization structure, reduce friction, and ensure clients get a consistent experience for every delivery.",
        points: [
            {
                title: "Unified",
                hasAnimation: true,
                titleSuffix: " environment to serve as your source of truth"
            },
            {
                title: "Governance playbook for consistent delivery"
            },
            {
                title: "Templates and repeatable frameworks that speed up delivery and prevent teams from freestyling."
            }
        ]
    },
    {
        title: "Eliminate Tool Sprawl",
        tabTitle: "Eliminate Sprawl",
        description: "Most companies are in “SaaS hell”. When too many tools are in play, teams spend more time switching between apps, information gets lost, and projects slow down. We help you consolidate and streamline your systems, giving your team an Operational Hub. Creating a smoother,  consistent way of working.",
        points: [
            {
                title: "A unified Operations Tool Stack that reduces costs and wasted time"
            },
            {
                title: "Streamlined workflows that replace scattered apps and friction"
            },
            {
                title: "Documentation and team training for smooth adoption"
            }
        ]
    },
    {
        title: "Avoid Expensive Trial-and-Error PMO Builds",
        tabTitle: "Avoid Expense",
        description: "Building a PMO in house often leads to alot of guesswork, teams reinventing the wheel, testing tools they never fully adopt, and creating systems that do not scale. We remove trial and error by giving you a proven operational foundation that is ready to use and tailored to your organization.",
        points: [
            {
                title: "A complete PMO framework that replaces months of experimentation with a clear starting point"
            },
            {
                title: "75% faster and less expensive implementation than in-house builds"
            },
            {
                title: "Real-time ROI tracking that shows how your PMO is performing"
            }
        ]
    }
]


const Tabs = () => {
    const [activeTab, setActiveTab] = useState(0);
    const tabRefs = useRef([]);

    const handleTabClick = (index) => {
        setActiveTab(index);

        if (tabRefs.current[index]) {
            tabRefs.current[index].scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest',
                inline: 'start' 
            });
        }
    }

    return (
        <div className="w-full px-4 md:px-8 lg:px-16">
            <div className="w-full flex flex-col gap-10 py-6 px-4 md:p-12 lg:py-20 lg:px-6 bg-primaryBrand rounded-3xl mx-auto container">
                <div className="relative w-full flex flex-col gap-4 justify-start items-center">
                    <div className="w-full flex flex-row gap-4 lg:justify-center items-center lg:flex-wrap overflow-y-scroll hide-scroll scroll-smooth lg:overflow-x-visible">
                        {tabsContent.map((tab, index) => (
                            <div key={index} ref={el => tabRefs.current[index] = el} className={"w-max min-w-max flex flex-row justify-center items-center py-3 px-6 lg:px-7 rounded-lg cursor-pointer " + (activeTab === index ? " bg-blue30" : " bg-blue23")} onClick={() => handleTabClick(index)}>
                                <p className="text-white text-base font-normal">{tab.tabTitle}</p>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => handleTabClick(activeTab - 1)} disabled={activeTab === 0} className={"cursor-pointer bg-gradient-to-r from-primaryBrand to-transparent w-16 h-full flex justify-start items-center absolute left-0 lg:hidden" + (activeTab === 0 ? " hidden" : "")}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <path d="M25 10L15 20L25 30" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button onClick={() => handleTabClick(activeTab + 1)} disabled={activeTab === tabsContent.length - 1} className={"cursor-pointer bg-gradient-to-l from-primaryBrand to-transparent w-16 h-full flex justify-end items-center absolute right-0 lg:hidden" + (activeTab === tabsContent.length - 1 ? " hidden" : "")}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <path d="M15 10L25 20L15 30" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    
                </div>
                <div className="w-full h-[680px] md:h-[540px] lg:h-[458px] relative">
                    {tabsContent.map((tab, index) => (
                        <div key={index} className={"w-full h-full flex flex-col items-center justify-center gap-4 absolute top-0 left-0 transition-all duration-500 ease-in-out" + (activeTab === index ? " opacity-100" : " opacity-0")}>
                            <div className="w-full max-w-3xl h-full flex flex-col gap-5 text-left items-start justify-start">
                                <h3 className="text-white font-normal text-xl md:text-3xl">{tab.title}</h3>
                                <p className="text-white font-normal leading-relaxed  md:leading-relaxed text-base md:text-2xl">{tab.description}</p>
                                <hr className="w-full border-white"/>
                                <ul className="list-disc list-inside w-full gap-2 flex flex-col items-start justify-start">
                                    <h4 className="text-white md:text-sm text-xs font-normal uppercase ">Achieve Better With</h4>
                                    {tab.points.map((point, index) => (
                                        <li key={index} className="flex flex-row gap-4 items-center justify-start">
                                            <div className="w-6 h-6 flex justify-center p-0.5 items-center bg-white rounded-full aspect-square">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none">
                                                    <path d="M1 4.75L4.75 8.5L12.25 1" stroke="#182553" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </div>
                                            <p className="text-white text-base md:text-2xl font-normal leading-relaxed">
                                                {point.hasAnimation ? (
                                                    <>
                                                        {point.title} <AnimationText />{point.titleSuffix}
                                                    </>
                                                ) : (
                                                    point.title
                                                )}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Tabs;